
import React from 'react'
import moment from 'moment'

import useTranslation from '@/Hooks/UseTranslation'

import './WeekNavigator.scss'

interface Props {
  prev: string
  next: string
  current: string | number
  label?: string
  range?: string
  startDate?: string
  endDate?: string
  onChangeWeek: (week?: string) => void
}

const compactMonth = (date: moment.Moment) => date.format('MMM').replace('.', '')

export const getCompactWeekText = (
  current: string | number,
  language: string,
  startDate?: string,
  endDate?: string,
) => {
  const currentWeek = String(current || '')
  const weekNumber = Number(currentWeek.slice(-2)) || currentWeek
  const locale = language || 'en'
  const fallbackWeek = moment(currentWeek, 'YYYYWW').locale(locale)
  const start = (startDate ? moment(startDate) : fallbackWeek.clone().startOf('isoWeek')).locale(locale)
  const end = (endDate ? moment(endDate) : fallbackWeek.clone().endOf('isoWeek')).locale(locale)

  if (!start.isValid() || !end.isValid()) return `CW${weekNumber}`

  let range = ''

  if (start.isSame(end, 'month')) {
    range = `${start.format('D')}–${end.format('D')} ${compactMonth(end)}`
  } else if (start.isSame(end, 'year')) {
    range = `${start.format('D')} ${compactMonth(start)}–${end.format('D')} ${compactMonth(end)}`
  } else {
    range = `${start.format('D')} ${compactMonth(start)} ${start.format('YYYY')}–${end.format('D')} ${compactMonth(end)} ${end.format('YYYY')}`
  }

  return `CW${weekNumber} · ${range}`
}

const WeekNavigator = (props: Props) => {

  const { prev, next, current, label, range, startDate, endDate, onChangeWeek } = props

  const { language, t } = useTranslation()
  const compactText = label ? getCompactWeekText(current, language, startDate, endDate) : ''

  return (
    <div className={`week-navigator ${label ? 'pl4-cnc-week-control' : ''}`}>
      <button
        type="button"
        className={`btn btn-link btn-navigator btn-prev ${!prev && 'disabled'}`}
        disabled={!prev}
        onClick={() => onChangeWeek && onChangeWeek(prev)}
        aria-label={t('RESOURCES_CNC:ToolbarPreviousButton')}
        title={t('RESOURCES_CNC:ToolbarPreviousButton')}
      >
        <i className="icon-anterior" aria-hidden="true"></i>
        {!label && <span className="d-none d-md-inline"> {t('RESOURCES_CNC:ToolbarPreviousButton')}</span>}
      </button>
      <button
        type="button"
        className="btn btn-link btn-navigator btn-current"
        disabled={!!label && !current}
        onClick={() => onChangeWeek && onChangeWeek()}
        aria-label={label ? `${label}${range ? `, ${range}` : ''}. ${t('SYSTEM:CurrentWeekButton')}` : t('SYSTEM:CurrentWeekButton')}
        title={t('SYSTEM:CurrentWeekButton')}
      >
        <i className="icon-semana_actual" aria-hidden="true"></i>
        {label && (
          <span className="pl4-cnc-week-control__label">
            <span className="pl4-cnc-week-control__desktop-label">{label}{range && <> <span aria-hidden="true">·</span> <span>{range}</span></>}</span>
            <span className="pl4-cnc-week-control__mobile-label">{compactText}</span>
            <span className="pl4-cnc-week-control__hint">{t('SYSTEM:CurrentWeekButton')}</span>
          </span>
        )}
      </button>
      <button
        type="button"
        className={`btn btn-link btn-navigator btn-next ${!next && 'disabled'}`}
        disabled={!next}
        onClick={() => onChangeWeek && onChangeWeek(next)}
        aria-label={t('RESOURCES_CNC:ToolbarNextButton')}
        title={t('RESOURCES_CNC:ToolbarNextButton')}
      >
        {!label && <span className="d-none d-md-inline">{t('RESOURCES_CNC:ToolbarNextButton')} </span>}
        <i className="icon-siguiente" aria-hidden="true"></i>
      </button>
    </div>
  )
}

export default WeekNavigator
